import { Component, OnDestroy } from '@angular/core';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import {
  PICTURE_MANAGER_TOKEN,
  PictureManagerComponent,
} from '../../../reusable-components/picture-manager/picture-manager.component';
import { FormsModule } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { FooterService } from '../../../reusable-components/footer/footer.service';
import { MatStepperService } from '../lead-task-services/mat-stepper.service';
import { LeadTaskService } from '../lead-task.service';
import { GeneralInformationService } from './general-information.service';
import { ErrorListService } from '../../../reusable-components/error-list/error-list.service';
import { ErrorListComponent } from '../../../reusable-components/error-list/error-list.component';
import { LeadDashboardService } from '../../lead-dashboard.service';
import { GeolocationService } from '../../../services/geolocation/geolocation.service';
import { DialogService } from '../../../services/dialog/dialog.service';
import {
  playbackDeviceLocations,
  TOO_FAR_REASONS,
  TooFarReason,
} from '../../../examinationResult.model';
import { ExaminationNotPossibleComponent } from './examination-not-possible/examination-not-possible.component';

@Component({
  selector: 'app-general-information',
  imports: [
    MatAccordion,
    MatCheckbox,
    MatExpansionPanelHeader,
    MatExpansionPanel,
    MatFormField,
    MatExpansionPanelTitle,
    MatLabel,
    MatHint,
    MatInput,
    MatOption,
    MatSelect,
    TranslatePipe,
    PictureManagerComponent,
    FormsModule,
    ErrorListComponent,
    ExaminationNotPossibleComponent,
  ],
  templateUrl: './general-information.component.html',
  styleUrl: './general-information.component.scss',
  providers: [
    {
      provide: PICTURE_MANAGER_TOKEN,
      useFactory: (
        leadTaskService: LeadTaskService,
        generalInformationService: GeneralInformationService,
      ) => {
        return {
          getImages: () => leadTaskService.getImages(),
          addImage: (image: string) =>
            generalInformationService.addImage(image),
          removeImageWithDescription: (index: number) =>
            generalInformationService.removeImageWithDescription(index),
          addDescription: (index: number, description: string) =>
            leadTaskService.addDescription(index, description),
          getDescription: (index: number) =>
            leadTaskService.getDescription(index),
        };
      },
      deps: [LeadTaskService, GeneralInformationService],
    },
    GeneralInformationService,
    {
      provide: ErrorListService,
      useFactory: (leadTaskService: LeadTaskService) =>
        leadTaskService.errorListServiceGeneralInformation,
      deps: [LeadTaskService],
    },
  ],
})
export class GeneralInformationComponent implements OnDestroy {
  footerServiceSubscription: Subscription;
  readonly playbackDeviceLocations = playbackDeviceLocations;
  protected readonly Number = Number;

  constructor(
    protected leadTaskService: LeadTaskService,
    private footerService: FooterService,
    protected generalInformationService: GeneralInformationService,
    private matStepperService: MatStepperService,
    private leadDashboardService: LeadDashboardService,
    private geolocationService: GeolocationService,
    private dialogService: DialogService,
  ) {
    this.footerServiceSubscription = combineLatest([
      this.generalInformationService.active$,
      leadTaskService.errorListService.errors$,
      leadTaskService.submissionInProgress$,
    ]).subscribe(([active, errors, submissionInProgress]) => {
      if (active) {
        this.footerService.setLeftButton(
          this.matStepperService.getPreviousButton(),
        );
        this.footerService.setRightButton({
          label: 'components.general-information.finish-button',
          click: () => this.submitExaminationResult(),
          disabled: errors.length > 0 || submissionInProgress,
        });
      }
    });
  }

  async submitExaminationResult() {
    const currentLead = this.leadDashboardService.getCurrentLead();
    const currentPositionCloseEnough =
      await this.geolocationService.currentDistanceFromPositionCloseEnough(
        currentLead.coordinates,
      );
    const currentPosition = await this.geolocationService.getPosition();

    if (currentPositionCloseEnough) {
      this.submitAndFinishExamination(
        currentPosition.coords.latitude,
        currentPosition.coords.longitude,
      );
    } else {
      this.dialogService.openSelectDialog(
        TOO_FAR_REASONS.map((reason) => {
          return {
            option: reason,
            label: 'enums.too_far_reason.' + reason,
          };
        }),
        'components.selected-lead-list.too-far-label',
        'components.selected-lead-list.too-far-text',
        (reason) => {
          this.submitAndFinishExamination(
            currentPosition.coords.latitude,
            currentPosition.coords.longitude,
            reason as TooFarReason,
          );
        },
        'components.selected-lead-list.too-far-headline',
      );
    }
  }

  private submitAndFinishExamination(
    latitude: number,
    longitude: number,
    reason: TooFarReason | undefined = undefined,
  ) {
    // The examination is finished by LeadTaskService once the backend confirms
    this.leadTaskService.sendDraftToBackend(latitude, longitude, reason);
  }

  ngOnDestroy(): void {
    this.footerServiceSubscription.unsubscribe();
  }
}
